<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import { type Client, type UploadDriver, clientsApi, uploadDriversApi } from '@/api/entities'
import { clientActionsApi } from '@/api/entities/review/clientActions'

definePage({
  meta: {
    action: 'read',
    subject: 'Client',
  },
})

const { snackbar, handleError } = useErrorHandler()

// 상태
const page = ref(1)
const itemsPerPage = ref(15)
const totalItems = ref(0)
const searchQuery = ref('')
const loading = ref(false)
const clients = ref<Client[]>([])
const sortBy = ref<{ key: string; order: 'asc' | 'desc' }[]>([{ key: 'id', order: 'desc' }])

// 선택 상태
const selectedClients = ref<Client[]>([])

// Monthly State 다이얼로그
const showMonthlyStateDialog = ref(false)
const selectedMonthlyState = ref<'Y' | 'N'>('Y')
const updateDriversToo = ref(false)
const isUpdatingMonthlyState = ref(false)

// 필터
const uploadDrivers = ref<UploadDriver[]>([])
const selectedUploadDrivers = ref<UploadDriver[]>([])
const selectedType = ref<string | null>(null)

const types = [
  { title: '위젯', value: 'widget' },
  { title: '이관', value: 'migration' },
]

// 테이블 헤더
const headers = [
  { title: 'ID', key: 'id', sortable: true },
  { title: '업체명', key: 'name', sortable: true },
  { title: '유형', key: 'type', sortable: true },
  { title: 'DB명', key: 'target_database_hive', sortable: true },
  { title: 'PC 도메인', key: 'host', sortable: true },
  { title: '리뷰 솔루션', key: 'review_service', sortable: false },
  { title: '별점 기준', key: 'score', sortable: true },
  { title: '텍스트 리뷰', key: 'text', sortable: false },
  { title: '포토 리뷰', key: 'photo', sortable: false },
]

// 데이터 로드
const loadClients = async () => {
  loading.value = true
  try {
    // 선택된 업로드 드라이버 ID 배열 추출
    const reviewServiceIds = selectedUploadDrivers.value.length > 0
      ? selectedUploadDrivers.value.map((d: any) => typeof d === 'object' ? d.id : d)
      : undefined

    const response = await clientsApi.listClients({
      page: page.value,
      limit: itemsPerPage.value,
      query: searchQuery.value || undefined,
      review_services: reviewServiceIds,
      type: selectedType.value || undefined,
      with: ['uploadDriver'],
      sort: sortBy.value[0]?.key,
      order: sortBy.value[0]?.order,
    })

    if (response.success && response.data) {
      const data = response.data as any

      clients.value = data.data || data

      // Laravel Resource Collection은 meta.total에 전체 개수를 반환
      totalItems.value = data.meta?.total || data.total || clients.value.length
    }
  }
  catch (error) {
    handleError(error, { silent: true })
  }
  finally {
    loading.value = false
  }
}

const loadUploadDrivers = async () => {
  try {
    const response = await uploadDriversApi.listUploadDrivers({ per_page: 100 })
    if (response.success && response.data) {
      const data = response.data as any

      uploadDrivers.value = data.data || data
    }
  }
  catch (error) {
    handleError(error, { silent: true })
  }
}

// 검색 디바운스
const debouncedSearch = useDebounceFn(() => {
  page.value = 1
  loadClients()
}, 500)

// 감시자 - 페이지 변경
watch(page, () => {
  loadClients()
})

// 필터 변경 시 페이지 리셋 (page가 이미 1이면 직접 로드)
watch([itemsPerPage, selectedUploadDrivers, selectedType], () => {
  if (page.value === 1)
    loadClients()

  else
    page.value = 1 // watch(page)가 loadClients() 호출
})

// 정렬 변경 시 페이지 리셋
watch(sortBy, () => {
  if (page.value === 1)
    loadClients()

  else
    page.value = 1
}, { deep: true })

watch(searchQuery, () => {
  debouncedSearch()
})

// 초기화
onMounted(() => {
  loadClients()
  loadUploadDrivers()
})

// 유형 배지 색상
const getTypeBadgeColor = (type: string) => {
  return type === 'widget' ? 'success' : 'info'
}

const getTypeLabel = (type: string) => {
  return type === 'widget' ? '위젯' : '이관'
}

// 선택된 ID 목록
const selectedIds = computed(() => selectedClients.value.map(c => c.id))

// Monthly State 일괄 변경
const handleBulkUpdateMonthlyState = async () => {
  if (selectedIds.value.length === 0)
    return

  isUpdatingMonthlyState.value = true
  try {
    const response = await clientActionsApi.bulkUpdateMonthlyState(
      selectedIds.value,
      selectedMonthlyState.value,
      updateDriversToo.value,
    )

    if (response.success) {
      snackbar.success(`${selectedIds.value.length}개 고객사의 Monthly State가 변경되었습니다.`)
      showMonthlyStateDialog.value = false
      selectedClients.value = []
    }
    else {
      snackbar.error('Monthly State 변경에 실패했습니다.')
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: 'Monthly State 변경 중 오류가 발생했습니다.' })
  }
  finally {
    isUpdatingMonthlyState.value = false
  }
}

const openMonthlyStateDialog = () => {
  selectedMonthlyState.value = 'Y'
  updateDriversToo.value = false
  showMonthlyStateDialog.value = true
}
</script>

<template>
  <VCard>
    <VCardTitle class="d-flex align-center justify-space-between flex-wrap gap-4 pa-6">
      <span>고객사 관리</span>
      <VBtn
        color="primary"
        prepend-icon="tabler-plus"
        :to="{ name: 'review-client-create' }"
      >
        신규 고객사 등록
      </VBtn>
    </VCardTitle>

    <VDivider />

    <VCardText>
      <VRow class="mb-4">
        <VCol
          cols="12"
          md="3"
        >
          <AppTextField
            v-model="searchQuery"
            placeholder="검색어 입력"
            prepend-inner-icon="tabler-search"
            clearable
          />
        </VCol>
        <VCol
          cols="12"
          md="3"
        >
          <AppSelect
            v-model="selectedUploadDrivers"
            :items="uploadDrivers"
            item-title="driver_name"
            item-value="id"
            placeholder="리뷰 서비스"
            multiple
            chips
            closable-chips
            clearable
          />
        </VCol>
        <VCol
          cols="12"
          md="2"
        >
          <AppSelect
            v-model="selectedType"
            :items="types"
            placeholder="유형"
            clearable
          />
        </VCol>
      </VRow>

      <!-- 일괄 액션 바 -->
      <div
        v-if="selectedIds.length > 0"
        class="d-flex align-center gap-3 mb-4 pa-3 rounded bg-surface"
        style="border: 1px solid rgba(var(--v-theme-primary), 0.3);"
      >
        <span class="text-body-2 text-medium-emphasis">{{ selectedIds.length }}개 선택됨</span>
        <VBtn
          color="primary"
          size="small"
          prepend-icon="tabler-calendar"
          @click="openMonthlyStateDialog"
        >
          Monthly State 변경
        </VBtn>
        <VSpacer />
        <VBtn
          variant="text"
          size="small"
          @click="selectedClients = []"
        >
          선택 해제
        </VBtn>
      </div>

      <VDataTableServer
        v-model="selectedClients"
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        v-model:sort-by="sortBy"
        :headers="headers"
        :items="clients"
        :items-length="totalItems"
        :loading="loading"
        show-select
        return-object
        class="text-no-wrap"
      >
        <!-- 업체명 -->
        <template #item.name="{ item }">
          <RouterLink
            :to="{ name: 'review-client-id', params: { id: item.id } }"
            class="text-primary font-weight-medium"
          >
            {{ item.name }}
          </RouterLink>
        </template>

        <!-- 유형 -->
        <template #item.type="{ item }">
          <VChip
            :color="getTypeBadgeColor(item.type)"
            size="small"
          >
            {{ getTypeLabel(item.type) }}
          </VChip>
        </template>

        <!-- DB명 -->
        <template #item.target_database_hive="{ item }">
          <VChip
            color="secondary"
            size="small"
          >
            {{ item.target_database_hive }}
          </VChip>
        </template>

        <!-- 리뷰 솔루션 -->
        <template #item.review_service="{ item }">
          {{ item.upload_driver?.driver_name || '' }}
        </template>

        <!-- 별점 기준 -->
        <template #item.score="{ item }">
          <VRating
            :model-value="item.score"
            readonly
            density="compact"
            size="small"
          />
        </template>

        <!-- 텍스트 리뷰 -->
        <template #item.text="{ item }">
          <VIcon
            :icon="item.text === 'Y' ? 'tabler-check' : 'tabler-x'"
            :color="item.text === 'Y' ? 'success' : 'error'"
          />
        </template>

        <!-- 포토 리뷰 -->
        <template #item.photo="{ item }">
          <VIcon
            :icon="item.photo === 'Y' ? 'tabler-check' : 'tabler-x'"
            :color="item.photo === 'Y' ? 'success' : 'error'"
          />
        </template>
      </VDataTableServer>
    </VCardText>
  </VCard>

  <!-- Monthly State 일괄 변경 다이얼로그 -->
  <VDialog
    v-model="showMonthlyStateDialog"
    max-width="420"
  >
    <VCard>
      <VCardTitle class="pa-6">
        Monthly State 일괄 변경
      </VCardTitle>
      <VDivider />
      <VCardText class="pa-6">
        <p class="text-body-2 text-medium-emphasis mb-4">
          선택된 {{ selectedIds.length }}개 고객사의 Monthly State를 변경합니다.
        </p>
        <VRadioGroup
          v-model="selectedMonthlyState"
          inline
          class="mb-4"
        >
          <VRadio
            label="Y (활성)"
            value="Y"
          />
          <VRadio
            label="N (비활성)"
            value="N"
          />
        </VRadioGroup>
        <VCheckbox
          v-model="updateDriversToo"
          label="드라이버 설정도 함께 변경"
          density="compact"
          hide-details
        />
      </VCardText>
      <VCardActions class="pa-4">
        <VSpacer />
        <VBtn
          variant="text"
          @click="showMonthlyStateDialog = false"
        >
          취소
        </VBtn>
        <VBtn
          color="primary"
          :loading="isUpdatingMonthlyState"
          @click="handleBulkUpdateMonthlyState"
        >
          변경
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- 스낵바 -->
  <VSnackbar
    v-model="snackbar.isVisible.value"
    :color="snackbar.color.value"
    :timeout="snackbar.timeout.value"
  >
    {{ snackbar.message.value }}
  </VSnackbar>
</template>
